import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { PRODUCT_CLICK_LOCATION } from '@conversion/constants';
import { getImageMenu } from '@services/images.service';
import { timeDiffCalc, stringToDate } from '@utils/date';
import { isNullOrUndefined } from '@utils/object';
import message from './message';
import {
  Card,
  Image,
  Name,
  Price,
  DescriptionWrapper,
  PriceWrapper,
  ElapsedTime,
} from './components';

function ProductCard({
  product,
  onClick,
  intl: { formatMessage },
  menuSection,
}) {
  const productImage = useMemo(
    () =>
      getImageMenu({
        width: 64,
        height: 64,
        image: product.image,
      }),
    [product],
  );

  const elapsedTime = useMemo(() => {
    const { days } = timeDiffCalc(new Date(), stringToDate(product.orderDate));
    return days > 1 ? days : 1;
  }, [product]);

  const handleClick = useCallback(
    evt => {
      const clickLocation =
        (evt && evt.target.id) || PRODUCT_CLICK_LOCATION.CARD;
      onClick(product, clickLocation, menuSection);
    },
    [onClick, product, menuSection],
  );

  return (
    <Card id="preferred-dishes-card" direction="row" onClick={handleClick}>
      {!isNullOrUndefined(product?.image) ? (
        <Image src={productImage} alt={`preferred-dishes-${product?.name}`} />
      ) : null}
      <DescriptionWrapper>
        <Name>{product.name}</Name>
        <PriceWrapper>
          <ElapsedTime>
            {formatMessage(
              elapsedTime > 1
                ? message.product_card_more_days
                : message.product_card_one_day,
              {
                elapsedTime,
              },
            )}
          </ElapsedTime>
          <Price price={product.price} />
        </PriceWrapper>
        <Price
          price={null}
          finalPriceWithoutTaxes={product?.finalPriceWithoutTaxes}
        />
      </DescriptionWrapper>
    </Card>
  );
}

ProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  menuSection: PropTypes.string,
};

export default injectIntl(ProductCard);
